import React, { createContext, useContext, ReactNode, useState, useCallback } from 'react';
import i18n, { isLanguageSetByHost } from '../i18n';
import { GuestProfile, Auth0User } from '../types/auth';
import { hotelService, UserJourneyStageResponse } from '../services/hotel.service';

const USER_DATA_KEY = 'user_data';

const I18N_LANGUAGE_KEYS = ['en', 'es', 'fr', 'de', 'it', 'nl', 'ru', 'ar', 'ja', 'ko', 'zh-CN', 'pt-BR', 'pt-PT'];

function resolveI18nKey(apiCode: string): string | null {
  if (!apiCode) return null;
  const normalized = apiCode.trim();
  if (I18N_LANGUAGE_KEYS.includes(normalized)) return normalized;
  const base = normalized.split('-')[0].toLowerCase();
  const exact = I18N_LANGUAGE_KEYS.find((k) => k.toLowerCase() === base);
  if (exact) return exact;
  const regional = I18N_LANGUAGE_KEYS.find((k) => k.toLowerCase().startsWith(base + '-'));
  return regional || null;
}

interface UserData {
  id?: string;
  email: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  hasReservation?: boolean;
  checkInDate?: string;
  confirmationNumber?: string;
  hotelId?: string;
  hotelName?: string;
  journeyStage?: 'prospect' | 'pre-stay' | 'in-stay' | 'post-stay';
  profile?: GuestProfile;
  isVerified?: boolean;
  // Auth0 specific fields
  auth0User?: Auth0User;
  authMethod?: 'passkey' | 'biometric' | 'magic_link';
  lastAuthUpdate?: string;
  // User preferences
  responseType?: 'quick' | 'balanced' | 'detailed';
}

interface UserContextType {
  // Current user data state
  userData: UserData | null;
  // Get user data from localStorage
  getUserData: () => UserData | null;
  // Store user data to localStorage
  setUserData: (userData: UserData) => void;
  // Update existing user data in localStorage
  updateUserData: (updates: Partial<UserData>) => void;
  // Delete user data from localStorage
  clearUserData: () => void;
  // Auth0 specific methods
  setAuth0User: (auth0User: Auth0User, authMethod?: string) => void;
  convertAuth0ToUserData: (auth0User: Auth0User) => UserData;
  // Journey stage management
  journeyStageData: UserJourneyStageResponse | null;
  fetchJourneyStage: (hotelId: string) => Promise<void>;
  refreshJourneyStage: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error(i18n.t('contexts.userContext.useUserOutsideProvider'));
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [userData, setUserDataState] = useState<UserData | null>(() => {
    try {
      const stored = localStorage.getItem(USER_DATA_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Failed to get user data from localStorage:', error);
      return null;
    }
  });

  const [journeyStageData, setJourneyStageData] = useState<UserJourneyStageResponse | null>(null);
  const [currentHotelId, setCurrentHotelId] = useState<string | null>(null);
  
  const getUserData = useCallback((): UserData | null => {
    try {
      const stored = localStorage.getItem(USER_DATA_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Failed to get user data from localStorage:', error);
      return null;
    }
  }, []);

  const setUserData = useCallback((newUserData: UserData): void => {
    try {
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(newUserData));
      setUserDataState(newUserData);
    } catch (error) {
      console.error('Failed to save user data to localStorage:', error);
    }
  }, []);

  const updateUserData = useCallback((updates: Partial<UserData>): void => {
    try {
      const current = getUserData();
      if (current) {
        const updated = { ...current, ...updates };
        setUserData(updated);
      } else {
        // If no existing data, create new with required email field
        if (updates.email) {
          setUserData(updates as UserData);
        }
      }
    } catch (error) {
      console.error('Failed to update user data:', error);
    }
  }, [getUserData, setUserData]);

  const clearUserData = useCallback((): void => {
    try {
      localStorage.removeItem(USER_DATA_KEY);
      setUserDataState(null);
    } catch (error) {
      console.error('Failed to clear user data:', error);
    }
  }, []);
  
  const convertAuth0ToUserData = useCallback((auth0User: Auth0User): UserData => {
    return {
      id: auth0User.sub,
      email: auth0User.email,
      fullName: auth0User.name,
      firstName: auth0User.given_name,
      lastName: auth0User.family_name,
      isVerified: auth0User.email_verified,
      journeyStage: auth0User.app_metadata?.journeyStage || 'prospect',
      hasReservation: !!auth0User.app_metadata?.currentReservation,
      checkInDate: auth0User.app_metadata?.currentReservation?.checkInDate,
      confirmationNumber: auth0User.app_metadata?.currentReservation?.confirmationNumber,
      hotelId: auth0User.app_metadata?.hotelId,
      hotelName: auth0User.app_metadata?.hotelName,
      auth0User: auth0User,
      lastAuthUpdate: new Date().toISOString()
    };
  }, []);
  
  const setAuth0User = useCallback((auth0User: Auth0User, authMethod?: string): void => {
    const userData = convertAuth0ToUserData(auth0User);
    if (authMethod) {
      userData.authMethod = authMethod as any;
    }
    setUserData(userData);
  }, [convertAuth0ToUserData, setUserData]);

  // Fetch journey stage data
  const fetchJourneyStage = useCallback(async (hotelId: string): Promise<void> => {
    try {
      console.log('🚀 UserContext: Fetching journey stage for hotel:', hotelId);
      const data = await hotelService.getUserJourneyStage(hotelId);
      if (data) {
        console.log('✅ UserContext: Journey stage fetched:', data);
        setJourneyStageData(data);
        setCurrentHotelId(hotelId);

        const apiLang = (data as any)?.preferred_language?.code;
        if (apiLang && typeof apiLang === 'string') {
          const resolved = resolveI18nKey(apiLang);
          if (resolved && resolved !== i18n.language) {
            // Skip when the embedding host set the language this session —
            // via the URL ?lng= param at boot OR a runtime
            // cordiant:set-language message from the marketing parent. The
            // host's explicit choice wins, so the backend's persisted
            // preference shouldn't override it.
            if (isLanguageSetByHost()) {
              console.log('🌐 UserContext: Skipping API preferred_language (host choice wins):', {
                api: resolved,
                current: i18n.language,
              });
            } else {
              console.log('🌐 UserContext: Applying preferred_language from API:', resolved);
              i18n.changeLanguage(resolved);
            }
          }
        }
      }
    } catch (error) {
      console.error('❌ UserContext: Failed to fetch journey stage:', error);
      // Set default fallback
      setJourneyStageData({
        journey_stage: 'prospect',
        show_discover_menu: false
      });
    }
  }, []);

  // Refresh journey stage (re-fetch using stored hotelId)
  const refreshJourneyStage = useCallback(async (): Promise<void> => {
    if (currentHotelId) {
      console.log('🔄 UserContext: Refreshing journey stage for hotel:', currentHotelId);
      await fetchJourneyStage(currentHotelId);
    } else {
      console.warn('⚠️ UserContext: Cannot refresh journey stage - no hotelId stored');
    }
  }, [currentHotelId, fetchJourneyStage]);

  const value: UserContextType = {
    userData,
    getUserData,
    setUserData,
    updateUserData,
    clearUserData,
    setAuth0User,
    convertAuth0ToUserData,
    journeyStageData,
    fetchJourneyStage,
    refreshJourneyStage
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;